import React from "react";
import { Grid, styled, useTheme } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import MxIcons from "@rf-l1/MxIcons";

interface MxChangeLogArrowProps {
  /** Valor anterior */
  fromText: string | number | null;
  /** Valor nuevo */
  toText: string | number | null;
  /** Color del ícono flecha (opcional) */
  color?: string;
  /** Encabezado opcional encima de la fila */
  title?: string;
  /** Locale para formateo numérico y de fecha.  Por defecto es "es-DO". */
  locale?: string;
}

/**
 * Detecta el tipo de dato contenido en un string.
 */
const detectType = (value?: string | number | null): "date" | "number" | "text" => {
  if (value === null || value === undefined) return "text";
  if (typeof value === "number") return "number";

  const trimmed = String(value).trim();
  /** Número: admite separadores decimales punto o coma y cualquier cantidad de dígitos */
  const numberRegex = /^-?\d+(?:[.,]?\d+)?$/;
  /** ISO 8601  (YYYY-MM-DD) o variantes con hora   */
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/;
  /** dd/mm/yyyy o dd-mm-yyyy */
  const dmyDateRegex = /^\d{2}[-/]\d{2}[-/]\d{4}$/;

  if (numberRegex.test(trimmed)) return "number";
  if (isoDateRegex.test(trimmed) || dmyDateRegex.test(trimmed)) return "date";
  return "text";
};

/**
 * Aplica formato según el tipo de dato.
 *  - number → separador de miles/decimales según locale.
 *  - date   → fecha corta (ej.: 20 may 2025)
 */
const formatValue = (
  value: string,
  type: "date" | "number" | "text",
  locale: string
): string => {
  switch (type) {
    case "number": {
      // Normalizamos a punto decimal para parsear
      // const normalized = value.replace(/\./g, "");
      const num = Number(value);
      return isNaN(num) ? value : new Intl.NumberFormat(locale).format(num);
    }
    case "date": {
      let date: Date | null = null;
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        // ISO o variante
        date = new Date(value);
      } else {
        const parts = value.split(/[-/]/);
        if (parts.length === 3) {
          const [d, m, y] = parts.map(Number);
          date = new Date(y, m - 1, d);
        }
      }
      if (!date || isNaN(date.getTime())) {
        return value;
      }
      try {
        return new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "short",
          day: "2-digit"
        }).format(date);
      } catch {
        return value;
      }
    }
    default:
      return value;
  }
};

export const MxChangeLogArrow: React.FC<MxChangeLogArrowProps> = ({
  fromText,
  toText,
  title,
  color = "primary.main",
  locale = "es-DO"
}) => {
  const fromType = detectType(fromText);
  const toType = detectType(toText);
  const theme = useTheme();

  const formattedFrom =
    fromText === null || fromText === ""
      ? "Sin información"
      : formatValue(String(fromText), fromType, locale);
  const formattedTo =
    toText === null || toText === ""
      ? "Sin información"
      : formatValue(String(toText), toType, locale);

  return (
    <Grid container my={0.5} rowGap={1} px={1}>
      {title && (
        <Grid container item xs={12}>
          <MxTypography variant="caption" color={color} fontWeight={600}>
            {title}
          </MxTypography>
        </Grid>
      )}
      <Content>
        {/* Valor anterior */}
        <MxTypography
          className="data-set"
          variant="caption"
          color={"grey.700"}
          fontWeight={300}
          data-type={fromType}
          component="p"
          dangerouslySetInnerHTML={{
            __html: formattedFrom
          }}
        ></MxTypography>

        {/* Flecha */}
        <MxIcons color={color} size={20}>
          <IconArrowNarrowRight />
        </MxIcons>

        {/* Valor nuevo */}
        <MxTypography
          component="p"
          variant="caption"
          color={"grey.700"}
          fontWeight={300}
          data-type={toType}
          dangerouslySetInnerHTML={{
            __html: formattedTo
          }}
        ></MxTypography>
      </Content>
    </Grid>
  );
};

const Content = styled("div")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  "& .data-set": {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "wrap"
  },
  alignItems: "center",
  gap: theme.spacing(1.25), // ≈10 px
  paddingLeft: 10
}));
